import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import config from '@/config';
import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { FileViewer } from '@/design_system/FileViewer/FileViewer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconDone from '@/icons/Done.svg';
import IconPrint from '@/icons/Print.svg';
import { useSignArticlePickUpReceipt } from '@/models/article';
import {
  ArticleWithRelations,
  RequestWithRelations,
  useSignRequestPickUpReceipt,
} from '@/models/request';
import { SignatureDialog } from '@/routes/Requests/components/SignatureDialog';
import useViewPort from '@/utils/useViewport';

export const PickUpReceiptDrawer = ({
  isOpen,
  setIsOpen,
  ...props
}: PickUpReceiptDrawerContentProps & {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  return (
    <Drawer isOpen={isOpen} onOpenChange={setIsOpen} position="bottom">
      <PickUpReceiptDrawerContent {...props} />
    </Drawer>
  );
};

type PickUpReceiptDrawerContentProps = {
  request: RequestWithRelations;
  // This component can be used on the request page or article page
  article?: ArticleWithRelations;
  onCompletePickUp: () => void;
  isLoadingCompletePickUp: boolean;
  completePickUpError: string;
};

const PickUpReceiptDrawerContent = ({
  request,
  article,
  onCompletePickUp,
  isLoadingCompletePickUp,
  completePickUpError,
}: PickUpReceiptDrawerContentProps) => {
  const { isMobile } = useViewPort();

  const [signedPickUpReceiptUrl, setSignedPickUpReceiptUrl] = useState<string>();

  const previewPickUpReceiptUrl =
    config.apiUrl +
    (article
      ? `/articles/${article.id}/pick-up-receipt`
      : `/requests/${request.id}/pick-up-receipt`);

  return (
    <>
      <DrawerHeader>
        <Title size="200" as="h1">
          <Trans id="pick-up-receipt.title">Pickup receipt</Trans>
        </Title>
      </DrawerHeader>
      <DrawerBody>
        <Stack gap="1rem" padding={isMobile ? '16px' : '24px 32px'} style={{ height: '100%' }}>
          {signedPickUpReceiptUrl ? (
            <Stack row gap="0.5rem">
              <IconDone className="color-primary-700" style={{ fontSize: '1.5rem' }} />
              <Paragraph color="secondary">
                <Trans id="pick-up-receipt.text.signed">
                  The pickup receipt has been successfully signed. You can now mark the pickup as
                  done.
                </Trans>
              </Paragraph>
            </Stack>
          ) : (
            <Paragraph color="secondary">
              <Trans id="pick-up-receipt.text">
                In order to complete the pickup process, please ask the client to sign the pickup
                receipt.
              </Trans>
            </Paragraph>
          )}
          <div style={{ flex: 1 }}>
            <FileViewer
              files={[
                {
                  id: 'pick-up-receipt',
                  type: 'pdf',
                  url: signedPickUpReceiptUrl ?? previewPickUpReceiptUrl,
                },
              ]}
              // This is needed for the FileViewer to re-render and display
              // the signed PDF when it becomes available
              key={signedPickUpReceiptUrl}
            />
          </div>
        </Stack>
      </DrawerBody>
      <Footer>
        {!!completePickUpError && <FooterError>{completePickUpError}</FooterError>}
        <FooterButtons>
          {!signedPickUpReceiptUrl && (
            <SignPickUpReceiptButton
              request={request}
              article={article}
              setSignedPickUpReceiptUrl={setSignedPickUpReceiptUrl}
            />
          )}
          {!!signedPickUpReceiptUrl && (
            <>
              <Button variant="secondary" href={signedPickUpReceiptUrl} download>
                <IconPrint />
                <Trans id="pick-up-receipt.download">Print pickup receipt</Trans>
              </Button>
              <Button
                variant="primary"
                onPress={onCompletePickUp}
                isLoading={isLoadingCompletePickUp}
              >
                <Trans id="pick-up-receipt.complete">Mark pickup as done</Trans>
              </Button>
            </>
          )}
        </FooterButtons>
      </Footer>
    </>
  );
};

const SignPickUpReceiptButton = ({
  request,
  article,
  setSignedPickUpReceiptUrl,
}: Pick<PickUpReceiptDrawerContentProps, 'request' | 'article'> & {
  setSignedPickUpReceiptUrl: (url: string) => void;
}) => {
  const { t } = useLingui();

  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    mutateAsync: signArticlePickUpReceipt,
    isPending: isPendingSignArticlePickUpReceipt,
    isSuccess: isSuccessSignArticlePickUpReceipt,
  } = useSignArticlePickUpReceipt({
    requestId: request.id,
    articleId: article?.id,
  });

  const {
    mutateAsync: signRequestPickUpReceipt,
    isPending: isPendingSignRequestPickUpReceipt,
    isSuccess: isSuccessSignRequestPickUpReceipt,
  } = useSignRequestPickUpReceipt({
    requestId: request.id,
  });

  const isLoadingSignPickUpReceipt = article
    ? isPendingSignArticlePickUpReceipt || isSuccessSignArticlePickUpReceipt
    : isPendingSignRequestPickUpReceipt || isSuccessSignRequestPickUpReceipt;

  const handleSignPickUpReceipt = async (signature: string) => {
    try {
      let url: string;

      if (article) {
        url = (await signArticlePickUpReceipt({ signature })).url;
      } else {
        url = (await signRequestPickUpReceipt({ signature })).url;
      }

      setSignedPickUpReceiptUrl(url);
      setIsSignatureDialogOpen(false);
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <Button variant="primary" onPress={() => setIsSignatureDialogOpen(true)}>
        <Trans id="pick-up-receipt.sign">Sign pickup receipt</Trans>
      </Button>
      <SignatureDialog
        isOpen={isSignatureDialogOpen}
        setIsOpen={setIsSignatureDialogOpen}
        title={<Trans id="pick-up-receipt.sign">Sign pickup receipt</Trans>}
        isSubmitLoading={isLoadingSignPickUpReceipt}
        onSubmit={handleSignPickUpReceipt}
        submitError={apiError}
      />
    </>
  );
};
